<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue'
import { type Supporter, PlatformIcon, TheTooltip, formatNumber,
  useMediaQuery, Dropdown, AccountResult, Popup, Parallel } from '@wastedondestiny/destiny-library'
import { otherExotics } from '../releaseDetail'
import collectibleDefinitions from '../generated/collectibleDefinitions.json'
import { CollectionAccount, LeaderboardPage } from '../types'
import { GroupV2 } from 'quria'
import { ActivityType, ExoticActivity } from '../enums/activities'
import ItemIcon from './ItemIcon.vue'
import writeXlsxFile, { SheetData, Cell } from 'write-excel-file'
import MemberBreakdown from './MemberBreakdown.vue'
import ProgressBadge from './ProgressBadge.vue'
import { ExoticSourceExpansion, ReleaseId } from '../enums/releases'
import { Expansions } from '../enums/expansions'
import { BeyondLightSeasonHashes, EdgeOfFateSeasonHashes, FinalShapeEpisodeHashes, LightfallSeasonHashes,
  RenegadesSeasonHashes, ShadowkeepSeasonHashes, WitchQueenSeasonHashes } from '../enums/seasons'

const isNotMobile = useMediaQuery('(min-width: 640px)')
const props = withDefaults(defineProps<{
  activities: ExoticActivity[],
  page?: LeaderboardPage,
  clan?: GroupV2,
  allClanMembers?: CollectionAccount[],
  loading?: boolean,
  filter: ActivityType,
  isDemo?: boolean
}>(), {
  isDemo: false
})
const emit = defineEmits(['onPageChange', 'removeClan', 'filter'])
const supporters = inject<Ref<Supporter[]>>('supporters')
const parallel = inject<Ref<Parallel[]>>('parallel')

const _totalPlayers = ref(0)
const totalPlayers = computed(() => {
  if (props.page?.total && props.page.total !== _totalPlayers.value) {
    _totalPlayers.value = props.page.total
  }

  return formatNumber(_totalPlayers.value)
})
const sortedActivities = computed(() => props.activities.filter(x => x.type === props.filter).sort((a, b) => b.date.getTime() - a.date.getTime()))

const renegadesExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.Renegades_Archive, ExoticSourceExpansion.Renegades_Season].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.Renegades,
      RenegadesSeasonHashes.Lawless
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const edgeOfFateExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.EdgeOfFate_Archive, ExoticSourceExpansion.EdgeOfFate_Quest].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.EdgeOfFate,
      EdgeOfFateSeasonHashes.Reclamation
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const finalShapeExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.FinalShape_Archive, ExoticSourceExpansion.FinalShape_Quest].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.FinalShape,
      FinalShapeEpisodeHashes.Echoes,
      FinalShapeEpisodeHashes.Revenant,
      FinalShapeEpisodeHashes.Heresy
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const lightfallExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.Lightfall_Archive, ExoticSourceExpansion.Lightfall_Quest].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.Lightfall,
      LightfallSeasonHashes.Defiance,
      LightfallSeasonHashes.Deep,
      LightfallSeasonHashes.Witch,
      LightfallSeasonHashes.Wish
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const witchQueenExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.WitchQueen_Archive, ExoticSourceExpansion.WitchQueen_Quest].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.TheWitchQueen,
      WitchQueenSeasonHashes.Risen,
      WitchQueenSeasonHashes.Haunted,
      WitchQueenSeasonHashes.Plunder,
      WitchQueenSeasonHashes.Seraph
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const beyondLightExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.BeyondLight_Archive, ExoticSourceExpansion.BeyondLight_Quest, ExoticSourceExpansion.BeyondLight_World].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.BeyondLight,
      BeyondLightSeasonHashes.Hunt,
      BeyondLightSeasonHashes.Chosen,
      BeyondLightSeasonHashes.Splicer,
      BeyondLightSeasonHashes.Lost
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const shadowkeepExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.Shadowkeep_Archive, ExoticSourceExpansion.Shadowkeep_Quest, ExoticSourceExpansion.Shadowkeep_World].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
      Expansions.Shadowkeep,
      ShadowkeepSeasonHashes.Undying,
      ShadowkeepSeasonHashes.Dawn,
      ShadowkeepSeasonHashes.Worthy,
      ShadowkeepSeasonHashes.Arrivals
    ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const forsakenExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.Forsaken_Archive, ExoticSourceExpansion.Forsaken_Quest, ExoticSourceExpansion.Forsaken_World].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
    Expansions.Forsaken,
    Expansions.AnnualPass
  ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const redWarExotics = [
  ...otherExotics.filter(x => [ExoticSourceExpansion.RedWar_Archive, ExoticSourceExpansion.RedWar_World].includes(x.source)).map(x => x.collectible),
  ...props.activities.filter(x => ([
    Expansions.Destiny2,
    Expansions.CurseOfOsiris,
    Expansions.Warmind
  ] as ReleaseId[]).includes(x.releasedWith)).map(x => x.collectible)
]
const miscExotics = [
  ...otherExotics.filter(x => x.source === ExoticSourceExpansion.Event_Quest).map(x => x.collectible),
  ...props.activities.filter(x => x.releasedWith === Expansions.Anniversary30th).map(x => x.collectible)
]

function firstPage() {
  emit('onPageChange', 1)
}

function previousPage() {
  emit('onPageChange', (props.page?.page || 2) - 1)
}

function nextPage() {
  emit('onPageChange', (props.page?.page || 1) + 1)
}

function removeClan() {
  emit('removeClan')
}

function checkOwnership(member: CollectionAccount, activity: ExoticActivity) {
  return !activity.ownership?.length ||
    activity.ownership.some(y => {
      const hasExpansion = ((member.versionsOwned ?? 0) & y) === y
      const hasSeason = member.seasonsOwned?.includes(y)
      const hasDungeon = member.dungeonsOwned?.includes(y)
      return hasExpansion || hasSeason || hasDungeon
    })
}

function filterActivities(type: ActivityType) {
  emit('filter', type)
}

function getWodLink(account: AccountResult) {
  return `https://wastedondestiny.com/${account.membershipId}`
}

function getCrLink(account: AccountResult) {
  return `https://crimson.report/${account.membershipId}`
}

function getParallel(account: AccountResult) {
  return parallel?.value.find(x => x.members.find(y => y.membershipId === account.membershipId))
}

function getDonation(account: AccountResult) {
  return supporters?.value.find(x => x.linkedMembershipIds?.includes(account.membershipId))
}

function getExotics(account: CollectionAccount, filter?: ActivityType) {
  const filteredExotics = filter !== undefined ? props.activities.filter(x => x.type === filter) : props.activities
  const obtainedExotics = filteredExotics.filter(x => account.items?.[x.collectible])
  return {
    none: obtainedExotics.length === 0,
    all: obtainedExotics.length === filteredExotics.length,
  }
}

function getTypeActivitySheet(activities: ExoticActivity[]): SheetData {
  return [
    [
      { value: 'Clan Name', fontWeight: 'bold' },
      { value: props.clan!.name, span: 4 }
    ],
    [],
    [
      { value: '' },
      ...activities.map(x => ({ value: x.name, fontWeight: 'bold' }) as Cell)
    ],
    [
      { value: 'Player Name', fontWeight: 'bold' },
      ...activities.map(x => ({ value: collectibleDefinitions?.find(y => y.id === x.collectible)?.name, fontStyle: 'italic' }) as Cell)
    ],
    ...props.allClanMembers!.map(x => ([
      { value: x.displayName },
      ...activities.map(y => {
        const owns = checkOwnership(x, y)
        if (x.items?.[y.collectible] && owns) {
          return { value: true, backgroundColor: '#008000' } as Cell
        } else if (owns) {
          return { value: false, backgroundColor: '#800000' } as Cell
        } else {
          return { } as Cell
        }
      })
    ]))
  ]
}

async function downloadExcel() {
  const sanitizedClanName = props.clan!.name.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const raids = props.activities.filter(x => x.type === ActivityType.Raid)
  const dungeons = props.activities.filter(x => x.type === ActivityType.Dungeon)
  const exoticMissions = props.activities.filter(x => x.type === ActivityType.ExoticMission)
  const data = [
    getTypeActivitySheet(raids),
    getTypeActivitySheet(dungeons),
    getTypeActivitySheet(exoticMissions)
  ]

  await writeXlsxFile(data, {
    columns: [
      [{ width: 20 }, ...raids.map(x => ({ width: 20 }))],
      [{ width: 20 }, ...dungeons.map(x => ({ width: 20 }))],
      [{ width: 20 }, ...exoticMissions.map(x => ({ width: 20 }))]
    ],
    sheets: ['Raids', 'Dungeons', 'Exotic Missions'],
    fileName: `${sanitizedClanName}.loot.report.xlsx`
  })
}
</script>

<template>
  <div class="w-full bg-white relative rounded-2xl">
    <ul class="flex flex-wrap">
      <li v-if="page && !loading" class="flex flex-col-reverse items-center sm:items-start text-black justify-between py-1 mx-2 sm:mr-0 border-b-zinc-200 border-b w-full sm:w-[calc(50%-8px)]">
        <div class="h-12 flex gap-x-2 flex-wrap justify-center">
          <span class="flex sm:h-12 items-center">
            Clan:
            <div class="ml-1 h-6 flex justify-between px-1 bg-zinc-300 rounded-md">
              {{ clan?.name }}
              <span @click="removeClan" class="cursor-pointer opacity-75 hover:opacity-100 ml-2 inline-flex items-center justify-center">
                <img alt="Close button" src="../assets/close.svg" class="h-4 w-4" />
              </span>
            </div>
          </span>
          <span class="sm:h-12 flex flex-col justify-center">Members: {{ totalPlayers }}</span>
        </div>
        <div class="flex items-center gap-1 h-10 overflow-hidden">
          <span class="leading-4 text-zinc-400 text-sm">Show only:</span>
          <button class="w-20 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filter === ActivityType.Raid, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filter !== ActivityType.Raid }" @click="() => filterActivities(ActivityType.Raid)">RAIDS</button>
          <button class="w-20 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filter === ActivityType.Dungeon, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filter !== ActivityType.Dungeon }" @click="() => filterActivities(ActivityType.Dungeon)">DUNGEONS</button>
          <button class="w-20 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filter === ActivityType.ExoticMission, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filter !== ActivityType.ExoticMission }" @click="() => filterActivities(ActivityType.ExoticMission)">EXOTIC MISSIONS</button>
        </div>
      </li>
      <li v-if="page && !loading" class="flex flex-col sm:flex-row text-black justify-between sm:items-center py-1 mx-2 sm:ml-0 h-28 leading-4 sm:leading-normal border-b-zinc-200 border-b w-full sm:w-[calc(50%-8px)]">
        <span></span>
        <div class="self-end flex gap-1">
          <div class="relative w-4 -rotate-[75deg]" v-for="activity in sortedActivities" :key="activity.id">
            <span class="text-nowrap text-[10px]">{{ collectibleDefinitions.find(x => x.id === activity.collectible)?.name }}</span>
          </div>
          <div class="w-4"></div>
        </div>
      </li>
      <li v-if="page && !loading" v-for="(player, i) in page.players" :key="player.membershipId"
        class="flex flex-col sm:flex-row text-black justify-between sm:items-center py-1 mx-2 h-12 leading-4 sm:leading-normal sm:h-8 border-b-zinc-200 border-b w-full">
        <span class="flex gap-2 items-center relative">
          <small class="w-8 text-xs text-zinc-400">{{ 1 + i + (10 * (page.page - 1)) }}</small>
          <PlatformIcon :account="player" :parallel="getParallel(player)" :supporting="getDonation(player)" />
          <template v-if="!isDemo">
            <Popup v-if="isNotMobile">
              <TheTooltip>
                <button class="decoration-1 decoration-dotted underline whitespace-nowrap">{{ player.displayName }}</button>
                <template #content>
                  <p class="w-52">Click to see a list of all exotics missing for this player.</p>
                </template>
              </TheTooltip>
              <template #content>
                <MemberBreakdown
                  :collectible-definitions="collectibleDefinitions"
                  :renegades-exotics="renegadesExotics"
                  :edge-of-fate-exotics="edgeOfFateExotics"
                  :final-shape-exotics="finalShapeExotics"
                  :lightfall-exotics="lightfallExotics"
                  :witch-queen-exotics="witchQueenExotics"
                  :beyond-light-exotics="beyondLightExotics"
                  :shadowkeep-exotics="shadowkeepExotics"
                  :forsaken-exotics="forsakenExotics"
                  :red-war-exotics="redWarExotics"
                  :misc-exotics="miscExotics"
                  :player="player"
                />
              </template>
            </Popup>
            <TheTooltip v-else url="-">
              <button class="decoration-1 decoration-dotted underline whitespace-nowrap">{{ player.displayName }}</button>
              <template #content="{ hide }">
                <p class="w-52">Click to see a list of all exotics missing for this player.</p>
                <div class="flex flex-col">
                  <Popup>
                    <button @click="hide" class="p-1 bg-zinc-800 w-full rounded-md mt-2 sm:w-28 h-8 text-white">CHECK EXOTICS</button>
                    <template #content>
                      <MemberBreakdown
                        :collectible-definitions="collectibleDefinitions"
                        :renegades-exotics="renegadesExotics"
                        :edge-of-fate-exotics="edgeOfFateExotics"
                        :final-shape-exotics="finalShapeExotics"
                        :lightfall-exotics="lightfallExotics"
                        :witch-queen-exotics="witchQueenExotics"
                        :beyond-light-exotics="beyondLightExotics"
                        :shadowkeep-exotics="shadowkeepExotics"
                        :forsaken-exotics="forsakenExotics"
                        :red-war-exotics="redWarExotics"
                        :misc-exotics="miscExotics"
                        :player="player"
                      />
                    </template>
                  </Popup>
                </div>
              </template>
            </TheTooltip>
          </template>
          <span v-else class="whitespace-nowrap">{{ player.displayName }}</span>
        </span>
        <div class="items-center justify-between w-full flex gap-1 mb-1">
          <div class="h-4 flex items-center gap-1 ml-16 sm:ml-2">
            <ProgressBadge v-if="getParallel(player)" name="Parallel" class="bg-zinc-800" description="Develop applications for Destiny or Destiny 2 and be recognized for it by Bungie" image="parallel.png" unlocked />
            
            <ProgressBadge v-if="getDonation(player)" name="Benefactor" class="bg-orange-600" inner-classes="!h-3 !w-3" description="Donate any amount towards Wasted on Destiny, BinarMorker or Karmageddon. Thank you!" image="seeker.png" unlocked :progress="getDonation(player)!.tier" />
            
            <ProgressBadge v-if="getExotics(player).none" name="Blueberry" class="bg-zinc-800" description="Have no exotic weapon from exotic missions, dungeons or raids" image="new.png" unlocked />
            
            <ProgressBadge v-if="getExotics(player, ActivityType.Raid).all" name="Raider" class="bg-stone-600" description="Have all the exotic weapons from raids" image="raid.png" unlocked />
            <ProgressBadge v-if="getExotics(player, ActivityType.Dungeon).all" name="Dungeoneer" class="bg-stone-600" description="Have all the exotic weapons from dungeons" image="dungeon.png" unlocked />
            <ProgressBadge v-if="getExotics(player, ActivityType.ExoticMission).all" name="Getting Started" class="bg-stone-600" description="Have all the exotic weapons from exotic missions" image="exotic.png" unlocked />
            <ProgressBadge v-if="getExotics(player).all" name="Collector" class="bg-stone-600" description="Have all the exotic weapons from exotic missions, dungeons and raids" image="all.png" unlocked />
          </div>
          <div class="flex gap-1">
            <div class="relative h-4 w-4" :class="{ 'opacity-50': !player.items[activity.collectible] }" v-for="activity in sortedActivities" :key="activity.id">
              <ItemIcon v-if="checkOwnership(player, activity) || player.items[activity.collectible]" small :item="collectibleDefinitions.find(x => x.id === activity.collectible)" />
              <img v-else src="../assets/close.svg" class="opacity-50" />
            </div>
            <div class="w-4"></div>
            <div class="absolute right-0" v-if="!isDemo">
              <div class="relative h-4">
                <Dropdown class="!-top-0.5" side width="18rem">
                  <a class="no-underline opacity-75 hover:opacity-100 p-2" target="_blank" :href="getWodLink(player)">
                    <img class="h-4 w-4 inline-block" src="../assets/twod.svg" /> Check time on Time Wasted on Destiny
                  </a>
                  <a class="no-underline opacity-75 hover:opacity-100 p-2 border-t-2 border-zinc-500" target="_blank" :href="getCrLink(player)">
                    <img class="h-4 w-4 inline-block" src="../assets/crimsonreport.svg" /> Check owned content on crimson.report
                  </a>
                  <a class="no-underline opacity-75 hover:opacity-100 p-2 border-t-2 border-zinc-500" target="_blank" href="https://www.bungie.net/7/en/User/Account/Privacy">
                    <img class="h-4 w-4 inline-block" src="../assets/close.svg" /> Change my privacy settings
                  </a>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </li>
      <li v-if="loading">
        <div class="flex flex-col gap-2 justify-center items-center h-[46.5rem] sm:h-[34rem]">
          <div class="text-3xl leading-7 font-serif font-bold flex gap-2">
            <span class="transition-opacity bullet-1">&bullet;</span>
            <span class="transition-opacity bullet-2">&bullet;</span>
            <span class="transition-opacity bullet-3">&bullet;</span>
          </div>
          <span class="text-black/50 text-xs">LOADING</span>
        </div>
      </li>
      <li v-if="page" class="flex justify-between items-center p-2 text-sm h-14 absolute bottom-0 left-0 right-0">
        <div v-if="page.page > 1" class="flex gap-2 w-[6.5rem] sm:w-52 rounded-bl-xl overflow-hidden">
          <button @click="firstPage" class="p-2 bg-zinc-800 w-12 sm:w-28 h-10 hover:bg-zinc-600">⇤<span class="hidden sm:inline"> FIRST</span></button>
          <button @click="previousPage" class="p-2 bg-zinc-800 w-12 sm:w-28 h-10 hover:bg-zinc-600">←<span class="hidden sm:inline"> PREVIOUS</span></button>
        </div>
        <span v-else class="w-[6.5rem] sm:w-52" />
        <small class="text-zinc-400 text-xs">{{ formatNumber(page.page) }} / {{ formatNumber(Math.ceil(_totalPlayers / 10)) }}</small>
        <div v-if="10 * (page.page) < _totalPlayers" class="flex justify-end gap-2 w-[6.5rem] sm:w-52 rounded-br-xl overflow-hidden">
          <button v-if="!isDemo" @click="downloadExcel" class="p-2 bg-emerald-800 w-12 sm:w-28 h-10 hover:bg-emerald-600"><span class="hidden sm:inline">EXPORT</span> <img class="h-3 w-3 -mt-0.5 inline-block invert" src="../assets/excel.svg" /></button>
          <button @click="nextPage" class="p-2 bg-zinc-800 w-12 sm:w-28 h-10" :class="{ 'hover:bg-zinc-600': !isDemo, 'cursor-default': isDemo }"><span class="hidden sm:inline">NEXT </span>→</button>
        </div>
        <span v-else class="w-[6.5rem] sm:w-52" />
      </li>
    </ul>
    <template v-if="isDemo && isNotMobile">
      <TheTooltip class="absolute" style="top: -2px; left: -2px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 52px; width: 340px;" />
        <template #content>
          <p class="w-52">The filters are clickable, and allow you to choose the activity type from which to show exotics.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 56px; left: 2px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 54px; width: 140px;" />
        <template #content>
          <p class="w-52">This is the current clan loaded and its number of members. Clicking on the close button on the card will remove the clan and allow you to search another one.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 114px; right: 22px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 27px; width: 209px;" />
        <template #content>
          <p class="w-52">Each line shows what exotics are owned by the player. A grayed-out icon means the player does not have the exotic yet, and a X means the player does not have access to the activity needed to acquire that exotic.</p>
        </template>
      </TheTooltip>
    </template>
    <template v-if="isDemo && !isNotMobile">
      <div class="w-full absolute flex flex-col items-center" style="top: 0;">
        <TheTooltip style="margin-top: -2px;">
          <div class="border-blue-500 border-2 rounded-md" style="height: 52px; width: 338px;" />
          <template #content>
            <p class="w-52">The filters are clickable, and allow you to choose the activity type from which to show exotics.</p>
          </template>
        </TheTooltip>
        <TheTooltip style="margin-top: 2px;">
          <div class="border-blue-500 border-2 rounded-md" style="height: 32px; width: 250px;" />
          <template #content>
            <p class="w-52">This is the current clan loaded and its number of members. Clicking on the close button on the card will remove the clan and allow you to search another one.</p>
          </template>
        </TheTooltip>
      </div>
      <TheTooltip class="absolute" style="top: 226px; right: 22px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 27px; width: 189px;" />
        <template #content>
            <p class="w-52">Each line shows what exotics are owned by the player. A grayed-out icon means the player does not have the exotic yet, and a X means the player does not have access to the activity needed to acquire that exotic.</p>
        </template>
      </TheTooltip>
    </template>
  </div>
</template>
